import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button
} from '@mui/material';

import {
    Chair,
    Kitchen,
    CheckCircleOutline,
    Groups,
    AttachMoney,
    ShoppingCart,
    InfoOutlined,
    Home,
    DevicesOther,
    Weekend,
    EmojiObjects,
    LocalDining,
    AssignmentTurnedIn
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const tocItems = [
    { id: 'introduction', label: 'はじめに' },
    { id: 'furniture-appliances', label: '必須の家具・家電リスト' },
    { id: 'joint-purchase-rules', label: '共同購入のルールづくり' },
    { id: 'shareable-items', label: 'シェアしやすいアイテムと選び方' },
    { id: 'distribution-methods', label: '分配・負担の決め方' },
    { id: 'expert-tips', label: 'ワンポイントアドバイス' },
    { id: 'summary', label: 'まとめ' }
];

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: 3 }}>
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)}>閉じる</Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            {tocItems.map((item, idx) => (
                                <ListItem key={item.id} sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{
                                        minWidth: 32,
                                        minHeight: 32,
                                        bgcolor: '#f5f5f5',
                                        color: '#888',
                                        fontWeight: 'bold',
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mr: 1,
                                        fontSize: '1.1rem'
                                    }}>
                                        {idx + 1}.
                                    </Box>
                                    <Link
                                        component="button"
                                        onClick={() => scrollToSection(item.id)}
                                        color="inherit"
                                        underline="hover"
                                        sx={{ cursor: 'pointer', fontSize: '0.95rem', textAlign: 'left', py: 0 }}
                                    >
                                        {item.label}
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}
            {/* --- 導入部分 --- */}
            <Box id="introduction" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    はじめに
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアを始めるとき、誰もが楽しみにしつつも頭を悩ませるのが「家具・家電」の選び方と購入方法です。<br />
                    「何を揃えればいいの？」「どうやって費用を分ける？」「みんなで使うものって、あとでどうする？」——そんな疑問や不安を解決するために、
                    本記事ではルームシェアの現場で役立つ実践的なノウハウを徹底解説します。
                </Typography>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6}>
                        <img
                            src="/images/blog/furniture-living-room_roomshare-kagu-kaden-kyoudou-kounyuu.jpg"
                            alt="明るい共有リビングでルームメイトたちが新しいソファを組み立てている様子。ナチュラルウッドと白を基調に、みんなが協力しながら楽しく作業している空間。"
                            style={{ width: '100%', borderRadius: 12, boxShadow: '0 2px 16px rgba(0,0,0,0.08)' }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            家具・家電の共同購入は、ルームシェア生活の快適さやトラブル防止のためにもとても大切。<br />
                            この記事を読めば、準備段階から実際の分配まで、迷わず進めるコツが身につきます。
                        </Typography>
                        <Box sx={{ mt: 2 }}>
                            <Chip icon={<Chair />} label="家具選び" color="primary" variant="outlined" sx={{ mr: 1 }} />
                            <Chip icon={<Kitchen />} label="家電選び" color="primary" variant="outlined" sx={{ mr: 1 }} />
                            <Chip icon={<Groups />} label="共同購入" color="primary" variant="outlined" />
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* --- 必須の家具・家電リスト --- */}
            <Box id="furniture-appliances" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    必須の家具・家電リスト
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアで「これだけは揃えておきたい」家具・家電をピックアップしました。<br />
                    共同生活の快適さや効率を高めるため、以下のリストを参考に必要なものをチェックしてみましょう。
                </Typography>
                <Paper elevation={2} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        <Home sx={{ mr: 1, verticalAlign: 'middle' }} />
                        共有リビング・ダイニングの必須アイテム
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon>
                                <Weekend color="primary" />
                            </ListItemIcon>
                            <ListItemText primary="ソファ・テーブル・チェア" secondary="みんなが集まる空間の快適さは家具次第" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <LocalDining color="primary" />
                            </ListItemIcon>
                            <ListItemText primary="ダイニングテーブルセット" secondary="食事・打ち合わせ・作業にも活躍" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <DevicesOther color="primary" />
                            </ListItemIcon>
                            <ListItemText primary="テレビ・Wi-Fiルーター" secondary="娯楽・情報共有のための電子機器" />
                        </ListItem>
                    </List>
                </Paper>
                <Paper elevation={2} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        <Kitchen sx={{ mr: 1, verticalAlign: 'middle' }} />
                        キッチン＆水回りの必須アイテム
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon>
                                <Kitchen color="primary" />
                            </ListItemIcon>
                            <ListItemText primary="冷蔵庫・電子レンジ・炊飯器" secondary="食事管理がラクになる大型家電" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <EmojiObjects color="primary" />
                            </ListItemIcon>
                            <ListItemText primary="電気ケトル・コーヒーメーカー" secondary="忙しい朝やリラックスタイムの必需品" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <CheckCircleOutline color="primary" />
                            </ListItemIcon>
                            <ListItemText primary="掃除機・洗濯機" secondary="共有スペースの清潔維持に不可欠" />
                        </ListItem>
                    </List>
                </Paper>
                <Paper elevation={2} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        <DevicesOther sx={{ mr: 1, verticalAlign: 'middle' }} />
                        その他あると便利なもの
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon>
                                <ShoppingCart color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="空気清浄機・加湿器"
                                secondary="季節や体調管理のためのサポート家電"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <Weekend color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="収納棚・シューズラック"
                                secondary="共有スペースの整理整頓に"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <InfoOutlined color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="ハンガー・物干し"
                                secondary="洗濯や衣類ケアに"
                            />
                        </ListItem>
                    </List>
                </Paper>
                <Typography variant="body2" color="text.secondary">
                    <CheckCircleOutline fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                    個人の部屋の家具（ベッドやデスク等）は各自で用意するのが一般的です。
                </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* --- 共同購入のルールづくり --- */}
            <Box id="joint-purchase-rules" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    共同購入のルールづくり
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    共同生活のトラブルを防ぐには、「買う前にルールを決めておく」ことが何より重要です。<br />
                    ここでは、よくある失敗例とともに、円滑に共同購入を進めるためのポイントを解説します。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={7}>
                        <List sx={{ mb: 1 }}>
                            <ListItem>
                                <ListItemIcon>
                                    <AssignmentTurnedIn color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="購入前に「何を・いくらで・誰が」買うかを明確に"
                                    secondary="カテゴリ別に持ち寄りや購入担当を決めるとスムーズ"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <AttachMoney color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="出資比率や支払い方法を事前に全員で合意"
                                    secondary="人数で割る・使用頻度で調整など柔軟な発想を"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <InfoOutlined color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="「後で分けるもの」「置いていくもの」の方針も決めておく"
                                    secondary="引越し時の処分や譲渡を想定しておくと安心"
                                />
                            </ListItem>
                        </List>
                    </Grid>
                    <Grid item xs={12} sm={5}>

                    </Grid>
                </Grid>
                <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        よくある失敗例
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon>
                                <InfoOutlined color="error" />
                            </ListItemIcon>
                            <ListItemText primary="購入後に「誰のもの？」でもめてしまう" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <InfoOutlined color="error" />
                            </ListItemIcon>
                            <ListItemText primary="買う人・払う人が偏って不公平感が生まれる" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <InfoOutlined color="error" />
                            </ListItemIcon>
                            <ListItemText primary="家電の処分や引き継ぎが決まらない" />
                        </ListItem>
                    </List>
                </Box>
                <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                        ルール作りのゴールは「納得感」と「記録」
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        口頭だけでなく、LINEグループやドキュメントで「共同購入リスト」を残しておくと安心です。
                    </Typography>
                </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* --- シェアしやすいアイテムと選び方 --- */}
            <Box id="shareable-items" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    シェアしやすいアイテムと選び方
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    全員で使うもの・個人で管理した方が良いもの——どこまでシェアするかの線引きも大切です。<br />
                    ここでは「共同購入に適したアイテム」と、「選ぶときのポイント」を紹介します。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                <Groups fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                                みんなでシェアしやすいアイテム
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleOutline color="success" />
                                    </ListItemIcon>
                                    <ListItemText primary="大型家具（ソファ・テーブル）" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleOutline color="success" />
                                    </ListItemIcon>
                                    <ListItemText primary="家電（冷蔵庫・洗濯機・掃除機）" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleOutline color="success" />
                                    </ListItemIcon>
                                    <ListItemText primary="調理家電（電子レンジ・炊飯器）" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleOutline color="success" />
                                    </ListItemIcon>
                                    <ListItemText primary="共有の食器・鍋類" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: '#f9fbe7', mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                <InfoOutlined fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                                個人管理をおすすめするアイテム
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleOutline color="disabled" />
                                    </ListItemIcon>
                                    <ListItemText primary="寝具（枕・布団・ベッド）" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleOutline color="disabled" />
                                    </ListItemIcon>
                                    <ListItemText primary="タオル・バス用品" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleOutline color="disabled" />
                                    </ListItemIcon>
                                    <ListItemText primary="個人の食器・マグカップ" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleOutline color="disabled" />
                                    </ListItemIcon>
                                    <ListItemText primary="小型家電（ドライヤー・加湿器など）" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
                <Box sx={{ mt: 2 }}>
                    <img
                        src="/images/blog/roommate-cooking_roomshare-kagu-kaden-kyoudou-kounyuu.jpg"
                        alt="白と木目調の明るいキッチンで、数人のルームメイトが共同で料理をしている。カウンターに調理家電や食器が整理されており、チームワークを感じるシーン。"
                        style={{ width: '100%', borderRadius: 12, boxShadow: '0 2px 16px rgba(0,0,0,0.08)' }}
                    />
                </Box>
                <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                        共同購入アイテムを選ぶときのポイント
                    </Typography>
                    <List>
                        <ListItem>
                            <ListItemIcon>
                                <InfoOutlined color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="耐久性とメンテナンス性を重視"
                                secondary="みんなで使うものは丈夫さ・掃除のしやすさが重要"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <ShoppingCart color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="購入時の保証・サポートを確認"
                                secondary="万一の故障やトラブル時も安心"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <AttachMoney color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="コストパフォーマンスを意識"
                                secondary="安すぎず高すぎず、全員が納得できるバランスを"
                            />
                        </ListItem>
                    </List>
                </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* --- 分配・負担の決め方 --- */}
            <Box id="distribution-methods" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    分配・負担の決め方
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    みんなで買ったもの、どうやってお金や所有権を分ければ良い？ ここでは代表的な分配方法と、注意点を紹介します。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={7}>
                        <List>
                            <ListItem>
                                <ListItemIcon>
                                    <AttachMoney color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="人数割り（均等）方式"
                                    secondary="もっともシンプルでトラブルが少ない。例：4人なら全員1/4ずつ負担。"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <Groups color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="利用頻度による調整"
                                    secondary="キッチン家電は料理好きの人が多く負担、など柔軟に。"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <ShoppingCart color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="アイテムごとに「持ち寄り」方式"
                                    secondary="Aさんがソファ、Bさんが冷蔵庫を用意し、費用は持ち主負担。"
                                />
                            </ListItem>
                        </List>
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                所有権・処分のルールも明確化
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                ・共同購入の場合、原則として「全員のもの」として扱い、退去時は売却・譲渡・置いていく等の方法を決めておきましょう。<br />
                                ・高額な家具家電は、レシートや保証書の管理も忘れずに。
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={5}>

                    </Grid>
                </Grid>
                <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                        ポイント
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon>
                                <CheckCircleOutline color="primary" />
                            </ListItemIcon>
                            <ListItemText primary="できるだけシンプルな分配ルールを選ぶ" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <CheckCircleOutline color="primary" />
                            </ListItemIcon>
                            <ListItemText primary="分配・管理方法はドキュメント化しておく" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <CheckCircleOutline color="primary" />
                            </ListItemIcon>
                            <ListItemText primary="後から不公平感が出にくい仕組みに" />
                        </ListItem>
                    </List>
                </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* --- ワンポイントアドバイス --- */}
            <Box id="expert-tips" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ワンポイントアドバイス
                </Typography>
                <Paper elevation={3} sx={{ p: 2, borderRadius: 2, bgcolor: '#fffde7' }}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        <EmojiObjects color="warning" sx={{ mr: 1, verticalAlign: 'middle' }} />
                        「ルームシェアの家具・家電こそ、みんなの“価値観”が現れるポイント」
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        ・好みやこだわりの強さ、予算感は人それぞれ。<br />
                        ・購入前に「何にこだわりたい？」「どこは妥協できる？」を全員で話し合うと、後の満足度が大きく変わります。<br />
                        ・こまめにコミュニケーションを取りながら、みんなが納得できる「モノ選び」と「分配ルール」を作りましょう。
                    </Typography>
                </Paper>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* --- まとめ --- */}
            <Box id="summary" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    まとめ
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    家具・家電の共同購入は、ルームシェア成功のカギ。「準備・話し合い・記録」を大切にすれば、トラブルをぐっと減らせます。<br />
                    みんなで快適な暮らしを実現するために、この記事のポイントをぜひ実践してみてください！
                </Typography>
                <Box sx={{ mt: 2, mb: 4 }}>
                    <Chip icon={<Chair />} label="家具" sx={{ mr: 1 }} />
                    <Chip icon={<DevicesOther />} label="家電" sx={{ mr: 1 }} />
                    <Chip icon={<Groups />} label="共同生活" sx={{ mr: 1 }} />
                    <Chip icon={<AttachMoney />} label="分担" />
                </Box>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-kagu-kaden-kyoudou-kounyuu',
    slug: 'roomshare-kagu-kaden-kyoudou-kounyuu',
    title: 'ルームシェアでの「家具・家電」選びと共同購入のコツ',
    excerpt: '必要な家具・家電、共同購入ルール、シェアしやすいアイテム、分配方法まで、ルームシェアを快適にするための実践ノウハウを徹底解説します。',
    coverImage: '/images/blog/cover-roomshare-furniture-appliances_roomshare-kagu-kaden-kyoudou-kounyuu.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが快適そうに会話し、中央にはセンスの良い家具・家電が配置されている様子。',
    publishedAt: '2024-06-26T00:00:00.000Z',
    isPublished: true,
    categories: ['ルームシェア', '暮らしの知恵', '家具・家電'],
    tags: ['ルームシェア', '家具', '家電', '共同購入', '分配方法'],
    author: {
        name: '佐藤 智美',
        avatar: '/images/avatars/satou-tomomi.jpg',
        bio: 'ルームシェア・共同生活の相談実績多数。暮らしに役立つ実践ノウハウと現場目線でのアドバイスが得意。',
        profilePicture: '/images/avatars/satou-tomomi.jpg'
    },
    viewCount: 1432,
    contentComponent: PostContent
};

export default post;